import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CmsInfluencerVideo, CmsProject } from '../../types/cms';
import { WebsiteCmsService } from '../../services/websiteCmsService';
import { generateVideoThumbnail, formatFileSize, detectVideoAspectRatio } from '../../utils/mediaOptimizer';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

interface InfluencerVideosManagerTabProps {
  videos: CmsInfluencerVideo[];
  projects?: CmsProject[];
  onSave: (item: CmsInfluencerVideo) => void;
  onDelete: (id: string) => void;
}

export default function InfluencerVideosManagerTab({
  videos,
  projects = [],
  onSave,
  onDelete,
}: InfluencerVideosManagerTabProps) {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CmsInfluencerVideo | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFileSize, setSelectedFileSize] = useState<number | null>(null);

  const filteredVideos = selectedFilter === 'all'
    ? videos
    : videos.filter((v) => v.projectSlug === selectedFilter);

  const handleToggleHomepage = (item: CmsInfluencerVideo) => {
    const updated = { ...item, featured: !item.featured };
    onSave(updated);
  };

  const handleOpenAdd = () => {
    setEditingItem({
      id: `inf-${Date.now()}`,
      title: '',
      creatorName: 'Creator Tour',
      videoUrl: '',
      thumbnailUrl: '',
      projectSlug: selectedFilter !== 'all' ? selectedFilter : (projects[0]?.slug || 'swarna-avsar'),
      aspectRatio: 'portrait',
      featured: true,
      order: videos.length,
    });
    setUploadProgress(0);
    setSelectedFileSize(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: CmsInfluencerVideo) => {
    setEditingItem({ ...item, aspectRatio: item.aspectRatio || 'portrait' });
    setUploadProgress(0);
    setSelectedFileSize(null);
    setDialogOpen(true);
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    setSelectedFileSize(file.size);
    setUploadingVideo(true);
    setUploadProgress(5);

    try {
      // 1. Auto-detect video aspect ratio (portrait vs landscape)
      const detectedRatio = await detectVideoAspectRatio(file);

      // 2. Auto-extract video thumbnail / poster frame in background
      let posterUrl = editingItem.thumbnailUrl || '';
      try {
        const thumbResult = await generateVideoThumbnail(file, 1.0);
        if (thumbResult) {
          posterUrl = await WebsiteCmsService.uploadMedia(
            thumbResult.file,
            editingItem.projectSlug || 'general',
            'reels/posters'
          );
        }
      } catch (thumbErr) {
        console.warn('Poster frame generation skipped:', thumbErr);
      }

      // 3. Upload video with live progress tracking
      const videoUrl = await WebsiteCmsService.uploadMedia(
        file,
        editingItem.projectSlug || 'general',
        'reels',
        (progress) => setUploadProgress(progress)
      );

      setEditingItem((prev) => (prev ? {
        ...prev,
        videoUrl,
        aspectRatio: detectedRatio,
        thumbnailUrl: posterUrl || prev.thumbnailUrl,
      } : null));
    } catch (err) {
      console.error('Video upload failed', err);
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const handleSave = () => {
    if (!editingItem || !editingItem.title) return;
    onSave(editingItem);
    setDialogOpen(false);
    setEditingItem(null);
  };

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header Banner */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          boxShadow: 'none',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbf9f6 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <VideoCameraFrontIcon sx={{ color: GOLD }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Master Creator & Walkthrough Reels
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#6b6b6b' }}>
              Master dashboard for all creator reels across all projects. Use the 1-click switch on any card to instantly turn Homepage visibility on or off.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              bgcolor: GOLD,
              '&:hover': { bgcolor: '#805926' },
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              borderRadius: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            Add Walkthrough Reel
          </Button>
        </Stack>

        {/* Project Filter Bar */}
        <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1, color: '#666' }}>
            <FilterListIcon sx={{ fontSize: 18, color: GOLD }} />
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Filter by Project:
            </Typography>
          </Box>

          <Chip
            label={`All Projects (${videos.length})`}
            onClick={() => setSelectedFilter('all')}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
              bgcolor: selectedFilter === 'all' ? GOLD : '#f3f4f6',
              color: selectedFilter === 'all' ? '#fff' : '#4b5563',
              '&:hover': { bgcolor: selectedFilter === 'all' ? '#805926' : '#e5e7eb' },
            }}
          />

          {projects.map((p) => {
            const count = videos.filter((v) => v.projectSlug === p.slug).length;
            const isSelected = selectedFilter === p.slug;
            return (
              <Chip
                key={p.slug}
                label={`${p.name} (${count})`}
                onClick={() => setSelectedFilter(p.slug)}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  bgcolor: isSelected ? GOLD : '#f3f4f6',
                  color: isSelected ? '#fff' : '#4b5563',
                  '&:hover': { bgcolor: isSelected ? '#805926' : '#e5e7eb' },
                }}
              />
            );
          })}
        </Box>
      </Paper>

      {/* Videos Grid */}
      {filteredVideos.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            border: `2px dashed ${BORDER}`,
            bgcolor: '#fafafa',
            boxShadow: 'none',
          }}
        >
          <VideoCameraFrontIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            No creator reels found {selectedFilter !== 'all' ? 'for this project' : ''}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b6b6b', maxWidth: 460, mx: 'auto', mb: 2.5 }}>
            Upload sample flat tours or vertical influencer walkthroughs to showcase them to buyers.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ bgcolor: GOLD, '&:hover': { bgcolor: '#805926' }, textTransform: 'none', fontWeight: 700 }}
          >
            Add Walkthrough Reel
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {filteredVideos.map((v) => {
            const projectObj = projects.find((p) => p.slug === v.projectSlug);
            const projectName = projectObj ? projectObj.name : (v.projectSlug === 'swarna-avsar' ? 'Swarna Avsar' : 'Swarna Lagom');

            return (
              <Card
                key={v.id}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {/* Media Player / Thumbnail */}
                <Box sx={{ position: 'relative', height: 210, bgcolor: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {v.videoUrl ? (
                    <video
                      src={v.videoUrl}
                      poster={v.thumbnailUrl}
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      preload="metadata"
                    />
                  ) : (
                    <VideoCameraFrontIcon sx={{ fontSize: 48, color: '#666' }} />
                  )}

                  {/* Top Badges */}
                  <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                    <Chip
                      label={v.aspectRatio === 'landscape' ? '🖥️ 16:9 Widescreen' : '📱 9:16 Reel'}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 10,
                        height: 22,
                        backdropFilter: 'blur(4px)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      }}
                    />
                  </Box>

                  <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                    {v.featured ? (
                      <Chip
                        icon={<HomeOutlinedIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />}
                        label="Live on Homepage"
                        size="small"
                        sx={{
                          bgcolor: GOLD,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 11,
                          height: 24,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<ApartmentIcon sx={{ fontSize: '14px !important', color: '#666 !important' }} />}
                        label="Project Page Only"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.9)',
                          color: '#444',
                          fontWeight: 600,
                          fontSize: 11,
                          height: 24,
                          backdropFilter: 'blur(4px)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Card Info */}
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: 15, mb: 0.5 }}>
                    {v.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: GOLD, fontWeight: 600, fontSize: 12 }}>
                    {v.creatorName ? `${v.creatorName} • ` : ''}{projectName}
                  </Typography>
                </CardContent>

                {/* Card Actions */}
                <CardActions
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderTop: `1px solid ${BORDER}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#fafafa',
                  }}
                >
                  {/* 1-Click Homepage Switch */}
                  <Tooltip title={v.featured ? 'Click to remove from Homepage' : 'Click to feature on Homepage'}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(v.featured)}
                          onChange={() => handleToggleHomepage(v)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GOLD },
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, color: v.featured ? GOLD : '#666' }}>
                          {v.featured ? 'Homepage ON' : 'Homepage OFF'}
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                  </Tooltip>

                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit Reel">
                      <IconButton size="small" onClick={() => handleOpenEdit(v)} sx={{ color: GOLD, '&:hover': { bgcolor: 'rgba(157,111,50,0.08)' } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Reel">
                      <IconButton size="small" onClick={() => onDelete(v.id)} sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a1a', borderBottom: `1px solid ${BORDER}` }}>
          {editingItem?.id.startsWith('inf-') ? 'Add Walkthrough Reel' : 'Edit Walkthrough Reel'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {editingItem && (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="Reel / Video Title"
                fullWidth
                size="small"
                value={editingItem.title}
                onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                placeholder="e.g. Sample Flat Luxury Living Tour"
                required
              />
              <TextField
                label="Creator / Host Name"
                fullWidth
                size="small"
                value={editingItem.creatorName}
                onChange={(e) => setEditingItem({ ...editingItem, creatorName: e.target.value })}
                placeholder="e.g. Architect Walkthrough or Creator Name"
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Associated Project</InputLabel>
                <Select
                  label="Associated Project"
                  value={editingItem.projectSlug || 'swarna-avsar'}
                  onChange={(e) => setEditingItem({ ...editingItem, projectSlug: e.target.value })}
                >
                  {projects.length > 0 ? (
                    projects.map((p) => (
                      <MenuItem key={p.slug} value={p.slug}>
                        {p.name}
                      </MenuItem>
                    ))
                  ) : (
                    <>
                      <MenuItem value="swarna-avsar">Swarna Avsar</MenuItem>
                      <MenuItem value="swarna-lagom">Swarna Lagom</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>

              {/* Video Orientation Selector */}
              <FormControl size="small" fullWidth>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 0.75, display: 'block', textTransform: 'uppercase' }}>
                  Video Orientation & Aspect Ratio
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Button
                    variant={editingItem.aspectRatio !== 'landscape' ? 'contained' : 'outlined'}
                    onClick={() => setEditingItem({ ...editingItem, aspectRatio: 'portrait' })}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 12.5,
                      py: 1,
                      bgcolor: editingItem.aspectRatio !== 'landscape' ? GOLD : '#fff',
                      color: editingItem.aspectRatio !== 'landscape' ? '#fff' : '#444',
                      borderColor: editingItem.aspectRatio !== 'landscape' ? GOLD : BORDER,
                      '&:hover': {
                        bgcolor: editingItem.aspectRatio !== 'landscape' ? '#805926' : '#f9f9f9',
                      },
                    }}
                  >
                    📱 9:16 Portrait Reel
                  </Button>

                  <Button
                    variant={editingItem.aspectRatio === 'landscape' ? 'contained' : 'outlined'}
                    onClick={() => setEditingItem({ ...editingItem, aspectRatio: 'landscape' })}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 12.5,
                      py: 1,
                      bgcolor: editingItem.aspectRatio === 'landscape' ? GOLD : '#fff',
                      color: editingItem.aspectRatio === 'landscape' ? '#fff' : '#444',
                      borderColor: editingItem.aspectRatio === 'landscape' ? GOLD : BORDER,
                      '&:hover': {
                        bgcolor: editingItem.aspectRatio === 'landscape' ? '#805926' : '#f9f9f9',
                      },
                    }}
                  >
                    🖥️ 16:9 Landscape Video
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ color: '#888', mt: 0.5 }}>
                  Auto-detected upon video upload, or switch manually.
                </Typography>
              </FormControl>

              {/* Video File Uploader */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 1, display: 'block', textTransform: 'uppercase' }}>
                  Walkthrough Reel Video (9:16 Vertical or 16:9 MP4 / MOV)
                </Typography>

                {selectedFileSize && selectedFileSize > 40 * 1024 * 1024 && (
                  <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                    Notice: File size is {formatFileSize(selectedFileSize)}. For optimal mobile playback, vertical reels under 30MB are recommended.
                  </Alert>
                )}

                {editingItem.videoUrl ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', border: `1px solid ${BORDER}` }}>
                    <Box sx={{ mb: 1.5, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#000', maxHeight: 200, display: 'flex', justifyContent: 'center' }}>
                      <video
                        src={editingItem.videoUrl}
                        poster={editingItem.thumbnailUrl}
                        controls
                        style={{ width: '100%', maxHeight: 200, objectFit: 'contain' }}
                      />
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 16 }} /> Video & Auto-Poster Saved
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          startIcon={uploadingVideo ? <CircularProgress size={14} /> : <VideoFileIcon />}
                          disabled={uploadingVideo}
                          sx={{ textTransform: 'none', borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: 'rgba(157,111,50,0.06)' } }}
                        >
                          Replace Reel
                          <input type="file" accept="video/mp4,video/quicktime,video/webm,video/m4v" hidden onChange={handleVideoUpload} />
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="text"
                          onClick={() => setEditingItem({ ...editingItem, videoUrl: '', thumbnailUrl: '' })}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : (
                  <Paper
                    component="label"
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderStyle: 'dashed',
                      borderColor: GOLD,
                      bgcolor: '#fdfbf9',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f7f1e7' },
                    }}
                  >
                    <input type="file" accept="video/mp4,video/quicktime,video/webm,video/m4v" hidden onChange={handleVideoUpload} />
                    {uploadingVideo ? (
                      <Box sx={{ width: '100%', textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" sx={{ color: GOLD, fontWeight: 700, mb: 1 }}>
                          Uploading Reel to Cloud: {uploadProgress}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#f0e6d9',
                            '& .MuiLinearProgress-bar': { bgcolor: GOLD },
                            mb: 1,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Extracting 1s poster frame for instant mobile preview...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 36, color: GOLD, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: GOLD }}>
                          Click to Upload Walkthrough Reel Video
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          Vertical 9:16 or 16:9 • MP4, MOV • Auto-extracts poster thumbnail
                        </Typography>
                      </>
                    )}
                  </Paper>
                )}
              </Box>

              <TextField
                label="Direct Video URL / Embed (Optional fallback)"
                fullWidth
                size="small"
                value={editingItem.videoUrl}
                onChange={(e) => setEditingItem({ ...editingItem, videoUrl: e.target.value })}
                helperText="Auto-populated on upload, or paste direct MP4/video link"
              />

              {/* Homepage Feature Toggle Box */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: editingItem.featured ? '#fdfbf9' : '#fafafa',
                  border: `1px solid ${editingItem.featured ? GOLD : BORDER}`,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(editingItem.featured)}
                      onChange={(e) => setEditingItem({ ...editingItem, featured: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: GOLD },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                        🌟 Show on Homepage Reel Carousel
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        {editingItem.featured
                          ? 'Will be displayed on both the Homepage (swarnagroup.in) and this project’s page.'
                          : 'Will only be displayed on the project’s dedicated page.'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#666', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={uploadingVideo}
            sx={{ bgcolor: GOLD, '&:hover': { bgcolor: '#805926' }, textTransform: 'none', fontWeight: 700 }}
          >
            Save Reel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
